'use client'

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import type { ProctoringEventType, ProctoringLeaveReason } from '@/constants/enums'
import { InterviewStreamHost, type InterviewStreamHostState, type RecordingStartedInfo } from '@/lib/interview-stream-host/interview-stream-host'
import type { InterviewStreamPluginInput, InterviewStreamHostPluginOptions, RecordingInitData } from '@/lib/interview-stream-host/interview-stream-host.type'
import { defineStreamPlugin } from '@/lib/interview-stream-host/interview-stream-host.type'
import { createCameraTrackMonitorPlugin } from '@/lib/interview-stream-host/plugins/camera-track-monitor.plugin'
import { createScreenShareMonitorPlugin } from '@/lib/interview-stream-host/plugins/screen-share-monitor.plugin'

export type UseInterviewStreamHostOptions = {
    enabled: boolean
    videoElementRef: RefObject<HTMLVideoElement | null>
    screenVideoElementRef?: RefObject<HTMLVideoElement | null>
    addProctoringEvent: (type: ProctoringEventType) => void
    onTerminate: (reason: ProctoringLeaveReason) => void
    /** When false, warnings still show but countdown timers and auto-leave are skipped. */
    enableProctoringCountdownLeave?: boolean
    /**
     * Alert kinds that start a countdown and auto-leave when enableProctoringCountdownLeave is true.
     * Defaults to all kinds.
     */
    countdownLeaveKinds?: Array<'no_face' | 'multiple_faces' | 'camera_off'>
    log: (message: string) => void
    /** Toggle built-in monitor plugins. Omitted or true means enabled. */
    plugins?: InterviewStreamHostPluginOptions
    extraPlugins?: InterviewStreamPluginInput[]
}

const defaultHostState: InterviewStreamHostState = {
    activeAlert: null,
    faceCount: 0,
    isScreenSharing: false,
    hasLocalVideo: false,
}

export function useInterviewStreamHost({
    enabled,
    videoElementRef,
    screenVideoElementRef,
    addProctoringEvent,
    onTerminate,
    enableProctoringCountdownLeave,
    countdownLeaveKinds,
    log,
    plugins: pluginOptions,
    extraPlugins = [],
}: UseInterviewStreamHostOptions) {
    const [hostState, setHostState] = useState<InterviewStreamHostState>(defaultHostState)
    const onTerminateRef = useRef(onTerminate)
    const addProctoringEventRef = useRef(addProctoringEvent)
    const logRef = useRef(log)
    onTerminateRef.current = onTerminate
    addProctoringEventRef.current = addProctoringEvent
    logRef.current = log

    const monitorPlugins = useMemo(
        () => [
            defineStreamPlugin(createCameraTrackMonitorPlugin(), pluginOptions?.camera !== false),
            defineStreamPlugin(createScreenShareMonitorPlugin(), pluginOptions?.screenShare !== false),
            ...extraPlugins,
        ],
        [extraPlugins, pluginOptions?.camera, pluginOptions?.screenShare]
    )

    const hostRef = useRef<InterviewStreamHost | null>(null)
    const setHostStateStable = useCallback((next: InterviewStreamHostState) => {
        setHostState((prev) => {
            const prevAlert = prev.activeAlert
            const nextAlert = next.activeAlert
            const alertUnchanged =
                prevAlert === nextAlert ||
                (prevAlert?.kind === nextAlert?.kind &&
                    prevAlert?.message === nextAlert?.message &&
                    prevAlert?.countdownSeconds === nextAlert?.countdownSeconds &&
                    prevAlert?.faceCount === nextAlert?.faceCount)

            if (
                prev.faceCount === next.faceCount &&
                prev.isScreenSharing === next.isScreenSharing &&
                prev.hasLocalVideo === next.hasLocalVideo &&
                alertUnchanged
            ) {
                return prev
            }
            return next
        })
    }, [])

    if (!hostRef.current) {
        hostRef.current = new InterviewStreamHost({
            plugins: monitorPlugins,
            addProctoringEvent: (type) => addProctoringEventRef.current(type),
            onTerminate: (reason) => onTerminateRef.current(reason),
            enableProctoringCountdownLeave,
            countdownLeaveKinds,
            log: (message) => logRef.current(message),
            onStateChange: setHostStateStable,
        })
    }

    useEffect(() => {
        hostRef.current?.setProctoringCountdownLeave(enableProctoringCountdownLeave !== false, countdownLeaveKinds)
    }, [enableProctoringCountdownLeave, countdownLeaveKinds])

    useEffect(() => {
        hostRef.current?.syncPluginRegistrations(monitorPlugins)
    }, [monitorPlugins])

    useEffect(() => {
        return () => {
            hostRef.current?.stop()
        }
    }, [])

    useEffect(() => {
        const host = hostRef.current
        if (!host) {
            return
        }
        if (enabled) {
            host.start()
        } else {
            host.stop()
        }
    }, [enabled])

    useEffect(() => {
        const screenEl = screenVideoElementRef?.current
        const screenTrack = hostRef.current?.getScreenTrack() ?? null
        if (!screenEl) {
            return
        }
        if (!hostState.isScreenSharing || !screenTrack) {
            screenEl.srcObject = null
            return
        }
        screenEl.srcObject = new MediaStream([screenTrack])
        screenEl.play().catch(() => undefined)
    }, [hostState.isScreenSharing, screenVideoElementRef])

    const attachCameraStream = useCallback((stream: MediaStream) => {
        hostRef.current?.attachCameraStream(stream)
    }, [])

    const detachCameraStream = useCallback(() => {
        hostRef.current?.detachCameraStream()
    }, [])

    const replaceCameraTrack = useCallback((track: MediaStreamTrack | null) => {
        hostRef.current?.replaceCameraTrack(track)
    }, [])

    const replaceAudioTrack = useCallback((track: MediaStreamTrack | null) => {
        hostRef.current?.replaceAudioTrack(track)
    }, [])

    const attachRemoteAudioStream = useCallback((stream: MediaStream) => {
        hostRef.current?.attachRemoteAudioStream(stream)
    }, [])

    const detachRemoteAudioStream = useCallback(() => {
        hostRef.current?.detachRemoteAudioStream()
    }, [])

    const stopCameraTracks = useCallback(() => {
        hostRef.current?.stopCameraTracks()
    }, [])

    const initializeRecording = useCallback(async (): Promise<RecordingInitData> => {
        const host = hostRef.current
        if (!host) {
            throw new Error('Stream host is not ready')
        }
        const data = await host.initializeRecording()
        host.setRecordingInit(data)
        return data
    }, [])

    const requestStartRecording = useCallback(() => {
        hostRef.current?.requestStartRecording()
    }, [])

    const tryStartRecording = useCallback(() => {
        hostRef.current?.tryStartRecording()
    }, [])

    const scheduleRecordingFallback = useCallback((delayMs: number) => {
        hostRef.current?.scheduleRecordingFallback(delayMs)
    }, [])

    const cancelRecordingFallback = useCallback(() => {
        hostRef.current?.cancelRecordingFallback()
    }, [])

    const setOnRecordingStarted = useCallback((callback: ((info: RecordingStartedInfo) => void) | null) => {
        hostRef.current?.setOnRecordingStarted(callback)
    }, [])

    const getRecordingStartedInfo = useCallback(() => {
        return hostRef.current?.getRecordingStartedInfo() ?? null
    }, [])

    const getMediaOffsetSeconds = useCallback(() => {
        return hostRef.current?.getMediaOffsetSeconds() ?? null
    }, [])

    const flushAndStopRecording = useCallback(async () => {
        await hostRef.current?.flushAndStopRecording()
    }, [])

    const stopAndFinalizeRecording = useCallback(async () => {
        await hostRef.current?.stopAndFinalizeRecording()
    }, [])

    const startScreenShare = useCallback(async () => {
        return (await hostRef.current?.startScreenShare()) ?? false
    }, [])

    const setPluginEnabled = useCallback((pluginId: string, pluginEnabled: boolean) => {
        hostRef.current?.setPluginEnabled(pluginId, pluginEnabled)
    }, [])

    const setLocalMicMuted = useCallback((muted: boolean) => {
        hostRef.current?.setLocalMicMuted(muted)
    }, [])

    const setVideoElement = useCallback(
        (element: HTMLVideoElement | null) => {
            videoElementRef.current = element
            hostRef.current?.setVideoElement(element)
        },
        [videoElementRef]
    )

    return {
        ...hostState,
        attachCameraStream,
        detachCameraStream,
        replaceCameraTrack,
        replaceAudioTrack,
        attachRemoteAudioStream,
        detachRemoteAudioStream,
        stopCameraTracks,
        initializeRecording,
        requestStartRecording,
        tryStartRecording,
        scheduleRecordingFallback,
        cancelRecordingFallback,
        setOnRecordingStarted,
        getRecordingStartedInfo,
        getMediaOffsetSeconds,
        flushAndStopRecording,
        stopAndFinalizeRecording,
        startScreenShare,
        setPluginEnabled,
        setLocalMicMuted,
        setVideoElement,
    }
}
